import { Stack } from '@mui/joy';
import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { Form } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { useFormContext } from 'core/states';
import { IPricingProfileAccessItem } from 'pricing/types';

import { Section } from './elements';
import { CustomersField, LocationsField, RegionsField, UsersField } from './fields';
import { RemovePricingProfileAccessButton } from './RemovePricingProfileAccessButton';
import { IPricingProfileAccessForm } from './types';
import { useAccessFieldArray } from './useAccessFieldArray';

interface IProps {
  pricingProfileAccessItem?: IPricingProfileAccessItem;
  isOpen: boolean;
  onClose: (reset?: UseFormReset<IPricingProfileAccessForm>) => void;
  onSubmit: (
    reset?: UseFormReset<IPricingProfileAccessForm>
  ) => (data: IPricingProfileAccessForm) => void;
  onRemoveSuccess?: () => void;
}

export const UpsertPricingProfileAccessFormFields = ({
  pricingProfileAccessItem,
  isOpen,
  onClose,
  onSubmit,
  onRemoveSuccess,
}: IProps) => {
  const { t } = useTranslation();

  const { user } = useAuthUser();

  const { watch, setValue } = useFormContext();
  const { customers, users, locations, regions } = useAccessFieldArray();

  const isAlternateVersionEnabled = Boolean(user?.alternateVersionEnabled);
  const customerCustomLabel = isAlternateVersionEnabled
    ? t('labelBillToCustomer')
    : t('labelCustomCustomers');

  const onAllCustomersChange = (value: boolean) => {
    setValue('allCustomers', value);
    setValue('customers', []);
  };

  const onAllUsersChange = (value: boolean) => {
    setValue('allUsers', value);
    setValue('users', []);

    if (!value) {
      setValue('allLocations', true);
      setValue('allRegions', true);
    }
  };

  const onAllLocationsChange = (value: boolean) => {
    setValue('allLocations', value);
    setValue('locations', []);

    if (!value) {
      setValue('allRegions', true);
    }
  };

  const onAllRegionsChange = (value: boolean) => {
    setValue('allRegions', value);
    setValue('regions', []);
  };

  const allCustomersValue = watch('allCustomers');
  const allUsersValue = watch('allUsers');
  const allLocationsValue = watch('allLocations');
  const allRegionsValue = watch('allRegions');

  const title = useMemo(() => {
    return pricingProfileAccessItem
      ? t('titleUpdatePricingProfileAccess')
      : t('titleAddPricingProfileAccess');
  }, [pricingProfileAccessItem, t]);

  const removeButton = useMemo(() => {
    return pricingProfileAccessItem ? (
      <RemovePricingProfileAccessButton
        pricingProfileAccessItems={[pricingProfileAccessItem]}
        buttonLabel={t('buttonLabelRemovePricingProfileAccess')}
        onRemoveSuccess={onRemoveSuccess}
        notificationPosition={NotificationPosition.FORM}
      />
    ) : null;
  }, [pricingProfileAccessItem, onRemoveSuccess, t]);

  return (
    <Form
      title={title}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      extraButtons={removeButton}
    >
      <Stack spacing={3}>
        <Section
          title={t('headerCustomers')}
          allValue={allCustomersValue}
          onAllChange={onAllCustomersChange}
          allLabel={t('labelAllCustomers')}
          customLabel={customerCustomLabel}
          addButtonLabel={t('buttonLabelAddCustomer')}
          onAdd={() =>
            customers.append({
              customerContactGroup: null,
              customerLocations: [],
            })
          }
        >
          <CustomersField customers={customers} />
        </Section>

        <Section
          title={t('headerUsers')}
          allValue={allUsersValue}
          onAllChange={onAllUsersChange}
          allLabel={t('labelAllUsers')}
          customLabel={t('labelCustomUsers')}
          addButtonLabel={t('buttonLabelAddUser')}
          onAdd={() =>
            users.append({
              user: null,
            })
          }
        >
          <UsersField users={users} />
        </Section>

        <Section
          title={t('headerLocations')}
          allValue={allLocationsValue}
          onAllChange={onAllLocationsChange}
          allLabel={t('labelAllLocations')}
          customLabel={t('labelCustomLocations')}
          addButtonLabel={t('buttonLabelAddLocation')}
          disabled={!allUsersValue}
          onAdd={() =>
            locations.append({
              location: null,
            })
          }
        >
          <LocationsField locations={locations} />
        </Section>

        <Section
          title={t('headerRegions')}
          allValue={allRegionsValue}
          onAllChange={onAllRegionsChange}
          allLabel={t('labelAllRegions')}
          customLabel={t('labelCustomRegions')}
          addButtonLabel={t('buttonLabelAddRegion')}
          disabled={!allUsersValue || !allLocationsValue}
          onAdd={() =>
            regions.append({
              region: null,
            })
          }
        >
          <RegionsField regions={regions} />
        </Section>
      </Stack>
    </Form>
  );
};
